/**
 * One imperative way to say "this action needs a verified phone" - the sibling of `signInPrompt`.
 *
 * The server is the authority: every citizen write that creates real platform activity (order,
 * booking, appointment, table, task/hire request, ...) sits behind `VerifiedCtx` and answers 403
 * `phone_not_verified` to an account with no verified number. The axios response interceptor
 * (lib/api.js) catches exactly that code in ONE place, calls `requestPhoneVerification()`, and -
 * once the person has verified in the inline `PhoneGate` sheet - replays the original request, so
 * the action simply continues. No call site needs its own phone check, and browsing never asks.
 */
let opener = null;
let pending = null;

export function onPhoneGate(fn) {
  opener = fn;
}

/** True when a `PhoneGate` sheet is mounted and can take over. */
export function phoneGateAvailable() {
  return Boolean(opener);
}

/** Opens the sheet; resolves true when the number was verified, false when dismissed.
 * Concurrent callers share the one sheet. */
export function requestPhoneVerification() {
  if (!opener) return Promise.resolve(false);
  if (!pending) {
    pending = new Promise((resolve) => opener(resolve)).finally(() => {
      pending = null;
    });
  }
  return pending;
}

/** The server's "verified phone required" error (`ErrorCode.PHONE_NOT_VERIFIED`). */
export function isPhoneNotVerified(error) {
  return error?.response?.status === 403 && error?.response?.data?.error?.code === "phone_not_verified";
}
